<template>
  <v-container fluid>
    <v-card class="pa-4">
      <v-card-title class="headline mb-4 d-flex justify-space-between align-center">
        <span>Изменения биллинга</span>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Добавить изменение
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="billingChanges"
          :loading="loading"
          :options.sync="options"
          :server-items-length="totalCount"
          :footer-props="{
            'items-per-page-options': [10, 20, 50, 100, -1],
            'items-per-page-text': 'Записей на странице:',
            'page-text': '{0}-{1} из {2}'
          }"
          class="elevation-1"
          @update:options="onOptionsChange"
        >
          <template v-slot:top>
            <v-text-field
              v-model="search"
              label="Поиск"
              class="mx-4"
              prepend-inner-icon="mdi-magnify"
              dense
              clearable
              @keyup.enter="onSearch"
              @click:clear="onSearch"
            />
          </template>

          <template v-slot:item.cargoTypeName="{ item }">
            {{ item.cargoTypeName || 'Не указан' }}
          </template>

          <template v-slot:item.billColumnName="{ item }">
            {{ item.billColumnName || '—' }}
          </template>

          <template v-slot:item.date="{ item }">
            {{ formatDate(item.date) }}
          </template>

          <template v-slot:item.qty="{ item }">
            {{ item.qty ?? 0 }}
          </template>
          
          <template v-slot:item.comment="{ item }">
            {{ item.comment || '—' }}
          </template>


          <template v-slot:item.actions="{ item }">
            <v-btn small color="primary" @click="openEditDialog(item)" class="mr-2">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn small color="error" @click="deleteItem(item)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon large color="grey lighten-2">mdi-database-off</v-icon>
              <div class="mt-2">Нет данных</div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Диалог добавления/редактирования -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title class="dialogTitleBackground white--text">
          <span>{{ formTitle }}</span>
          <v-spacer />
          <v-btn small icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form v-model="formValid" ref="form">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-if="editedIndex === -1"
                  v-model="editedItem.cargoTypeId"
                  :items="cargoTypes"
                  item-text="name"
                  item-value="id"
                  label="Контрагент *"
                  :rules="[requiredRule]"
                  dense
                />
                <v-text-field
                  v-else
                  :value="getCargoTypeName(editedItem.cargoTypeId)"
                  label="Контрагент"
                  disabled
                  dense
                />
              </v-col>

              <v-col cols="12">
                <v-select
                  v-if="editedIndex === -1"
                  v-model="editedItem.billColumnId"
                  :items="billColumns"
                  item-text="name"
                  item-value="id"
                  label="Графа *"
                  :rules="[requiredRule]"
                  dense
                />
                <v-text-field
                  v-else
                  :value="getBillColumnName(editedItem.billColumnId)"
                  label="Графа"
                  disabled
                  dense
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.qty"
                  label="Количество *"
                  type="number"
                  step="1"
                  min="0"
                  :rules="[requiredRule, qtyRule]"
                  dense
                />
              </v-col>

              <v-col cols="12">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFormatted"
                      label="Дата *"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[requiredRule]"
                      dense
                      clearable
                      @click:clear="editedItem.date = null"
                    />
                  </template>
                  <v-date-picker
                    v-model="editedItem.date"
                    no-title
                    scrollable
                    locale="ru-ru"
                    @input="dateMenu = false"
                  />
                </v-menu>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.comment"
                  label="Комментарий"
                  rows="3"
                  auto-grow
                  dense
                  clearable
                  counter="500"
                  maxlength="500"
                  hint="Необязательное поле"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" @click="closeDialog">Отмена</v-btn>
          <v-btn
            color="primary"
            :loading="saveLoading"
            @click="saveItem"
            :disabled="!formValid"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Диалог подтверждения удаления -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="dialogTitleBackground white--text">
          <span>Подтверждение удаления</span>
          <v-spacer />
          <v-btn small icon dark @click="deleteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <p>Вы уверены, что хотите удалить изменение биллинга?</p>
          <p class="font-weight-medium">ID: {{ deleteItemData?.id }}</p>
          <p v-if="deleteItemData">
            Контрагент: {{ deleteItemData.cargoTypeName || 'Не указан' }}
          </p>
          <p v-if="deleteItemData">
            Графа: {{ deleteItemData.billColumnName || '—' }}
          </p>
          <p v-if="deleteItemData">
            Количество: {{ deleteItemData.qty ?? 0 }}
          </p>
          <p v-if="deleteItemData">
            Дата: {{ formatDate(deleteItemData.date) }}
          </p>
          <p v-if="deleteItemData">
            Комментарий: {{ deleteItemData.comment || '—' }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" @click="deleteDialog = false">Отмена</v-btn>
          <v-btn color="error" :loading="deleteLoading" @click="confirmDelete">
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'BillingChangesList',

  data() {
    return {
      billingChanges: [],
      cargoTypes: [],
      billColumns: [],
      totalCount: 0,
      loading: false,
      search: '',
      searchDebounce: null,
      dialog: false,
      deleteDialog: false,
      editedIndex: -1,
      deleteItemData: null,
      formValid: false,
      saveLoading: false,
      deleteLoading: false,
      dateMenu: false,

      // Опции для v-data-table (server-режим)
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['date'],
        sortDesc: [true]
      },

      editedItem: {
        id: null,
        cargoTypeId: null,
        billColumnId: null,
        qty: null,
        date: null,
        comment: ''
      },
      defaultItem: {
        id: null,
        cargoTypeId: null,
        billColumnId: null,
        qty: null,
        date: null,
        comment: ''
      },
      headers: [
        { text: 'Контрагент', value: 'cargoTypeName', sortable: true },
        { text: 'Графа', value: 'billColumnName', sortable: true },
        { text: 'Дата', value: 'date', sortable: true },
        { text: 'Количество', value: 'qty', sortable: true },
        { text: 'Комментарий', value: 'comment', sortable: true },
        { text: 'Действия', value: 'actions', sortable: false, width: '150px' }
      ]
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? 'Добавить изменение' : 'Редактировать изменение';
    },
    dateFormatted() {
      if (!this.editedItem.date) return '';
      return this.formatDateForDisplay(this.editedItem.date);
    }
  },

  watch: {
    'editedItem.date'() {
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.validate();
        }
      });
    },
    search() {
      // Debounce поиска — 400 мс
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.onSearch();
      }, 400);
    }
  },

  async created() {
    await Promise.all([
      this.loadBillingChanges(),
      this.loadCargoTypes(),
      this.loadBillColumns()
    ]);
  },

  methods: {
    // ========== РАБОТА С ДАТАМИ ==========

    normalizeDate(date) {
      if (!date) return null;

      if (date instanceof Date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      if (typeof date === 'string') {
        const trimmedDate = date.trim();
        if (trimmedDate.includes('T')) return trimmedDate.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) return trimmedDate;
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmedDate)) {
          const [day, month, year] = trimmedDate.split('.');
          return `${year}-${month}-${day}`;
        }
      }

      return null;
    },

    formatDateForDisplay(date) {
      if (!date) return '';
      const normalized = this.normalizeDate(date);
      if (!normalized) return '';
      const [year, month, day] = normalized.split('-');
      return `${day}.${month}.${year}`;
    },

    formatDate(date) {
      return this.formatDateForDisplay(date) || '—';
    },

    // ========== ВАЛИДАЦИЯ ==========

    requiredRule(value) {
      return !!value || 'Это поле обязательно';
    },

    qtyRule(value) {
      if (value === null || value === undefined || value === '') {
        return 'Количество обязательно';
      }
      const num = Number(value);
      if (!Number.isInteger(num)) return 'Количество должно быть целым числом';
      if (isNaN(num)) return 'Количество должно быть числом';
      return true;
    },

    // ========== СПРАВОЧНИКИ ==========

    getCargoTypeName(id) {
      const cargoType = this.cargoTypes.find((c) => c.id === id);
      return cargoType ? cargoType.name : '';
    },

    getBillColumnName(id) {
      const billColumn = this.billColumns.find((c) => c.id === id);
      return billColumn ? billColumn.name : '';
    },

    // ========== ЗАГРУЗКА ДАННЫХ ==========

    async loadBillingChanges() {
      this.loading = true;
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options;

        // ✅ GET /api/BillingChanges/{pageNumber}/{pageSize}
        const response = await this.axios.get(
          `/BillingChanges/${page}/${itemsPerPage}`,
          {
            params: {
              sortBy: sortBy?.[0] || 'date',
              sortDesc: sortDesc?.[0] ?? true,
              search: this.search || null
            }
          }
        );

        // Backend возвращает { Items, TotalCount, PageNumber, PageSize, TotalPages }
        this.billingChanges = response.data.items || [];
        this.totalCount = response.data.totalCount || 0;
      } catch (error) {
        console.error('Ошибка загрузки изменений биллинга:', error);
        this.$notify('error', 'Ошибка загрузки данных');
        this.billingChanges = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    async loadCargoTypes() {
      try {
        const response = await this.axios.get('/CargoTypes');
        this.cargoTypes = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Ошибка загрузки типов грузов:', error);
        this.cargoTypes = [];
      }
    },

    async loadBillColumns() {
      try {
        const response = await this.axios.get('/Billing/bill-columns-to-select');
        this.billColumns = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Ошибка загрузки граф:', error);
        this.billColumns = [];
      }
    },

    // ========== ОБРАБОТКА СОБЫТИЙ ТАБЛИЦЫ ==========

    onOptionsChange(newOptions) {
      // Сохраняем новые опции (страница, размер, сортировка)
      this.options = { ...this.options, ...newOptions };
      this.loadBillingChanges();
    },

    onSearch() {
      // Сбрасываем на первую страницу и перезагружаем
      this.options.page = 1;
      this.loadBillingChanges();
    },

    // ========== ДИАЛОГИ ==========

    openCreateDialog() {
      this.editedIndex = -1;
      this.editedItem = {
        ...this.defaultItem,
        date: null,
        comment: ''
      };
      this.dialog = true;
    },

    openEditDialog(item) {
      this.editedIndex = this.billingChanges.indexOf(item);
      this.editedItem = {
        id: item.id,
        cargoTypeId: item.cargoTypeId,
        billColumnId: item.billColumnId,
        qty: item.qty,
        date: this.normalizeDate(item.date),
        comment: item.comment || ''
      };
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.editedItem = { ...this.defaultItem };
      this.editedIndex = -1;
      this.dateMenu = false;
    },

    // ========== СОХРАНЕНИЕ ==========

    async saveItem() {
      if (!this.$refs.form.validate()) return;

      const date = this.normalizeDate(this.editedItem.date);
      if (!date) {
        this.$notify('error', 'Пожалуйста, выберите корректную дату');
        return;
      }

      this.saveLoading = true;
      try {
        if (this.editedIndex === -1) {
          // ✅ POST /api/BillingChanges
          const payload = {
            billColumnId: this.editedItem.billColumnId,
            cargoTypeId: this.editedItem.cargoTypeId,
            date: date,
            qty: Number(this.editedItem.qty),
            comment: this.editedItem.comment?.trim() || null
          };
          await this.axios.post('/BillingChanges', payload);
          this.$notify('success', 'Изменение успешно добавлено');
        } else {
          // ✅ PATCH /api/BillingChanges/{id}
          const payload = {
            date: date,
            qty: Number(this.editedItem.qty),
            comment: this.editedItem.comment?.trim() || null
          };
          await this.axios.patch(`/BillingChanges/${this.editedItem.id}`, payload);
          this.$notify('success', 'Изменение успешно обновлено');
        }

        // Перезагружаем текущую страницу
        await this.loadBillingChanges();
        this.closeDialog();
      } catch (error) {
        console.error('Ошибка сохранения:', error);
        this.$notify('error', error.response?.data || 'Ошибка при сохранении');
      } finally {
        this.saveLoading = false;
      }
    },

    // ========== УДАЛЕНИЕ ==========

    deleteItem(item) {
      this.deleteItemData = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteLoading = true;
      try {
        // ✅ DELETE /api/BillingChanges/{id}
        await this.axios.delete(`/BillingChanges/${this.deleteItemData.id}`);

        this.$notify('success', 'Изменение успешно удалено');
        this.deleteDialog = false;
        this.deleteItemData = null;

        // Если удалили последнюю запись на странице — уходим на предыдущую
        const currentPageItems = this.billingChanges.length;
        if (currentPageItems === 1 && this.options.page > 1) {
          this.options.page -= 1;
        }

        await this.loadBillingChanges();
      } catch (error) {
        console.error('Ошибка удаления:', error);
        this.$notify('error', error.response?.data || 'Ошибка при удалении');
      } finally {
        this.deleteLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.dialogTitleBackground {
  background: #1976d2;
  padding: 16px 20px;
}
</style>